import { fetchWithAuth } from '../utils/fetchWithAuth';
import { unwrapResponse as unwrap } from '../utils/unwrapResponse';

/** `'*'` stands for every desk, and lives inside the list the API reads and writes. */
export type DeskAssignment = Array<number | '*'>;

/** What the actor may do with this row, as the server decided it (`userAccess.rowAbilities`). */
export interface UserAbilities {
  canEdit: boolean;
  canChangeRole: boolean;
  canEditServiceDesks: boolean;
  canGrantPermissions: boolean;
  assignableRoles: string[];
  grantablePermissions: string[];
}

/**
 * What the actor may do when there is no row yet (`userAccess.creationAbilities`). Same three
 * keys `UserAbilities` carries, so one card reads either without knowing which it got.
 */
export interface CreationRights {
  canEditServiceDesks: boolean;
  canGrantPermissions: boolean;
  grantablePermissions: string[];
}

/** The three fields the technician record writes — the account rights, never the business record. */
export interface UserRights {
  serviceDeskId: DeskAssignment;
  allowedTicketCreationDesks: number[];
  permissions: string[];
}

/** The role comes along: it names the account and decides which permissions its role already has. */
export interface UserRightsRecord {
  role: string;
  rights: UserRights;
  abilities: UserAbilities;
}

const jsonHeaders = { 'Content-Type': 'application/json' };

const ABILITIES: UserAbilities = {
  canEdit: false,
  canChangeRole: false,
  canEditServiceDesks: false,
  canGrantPermissions: false,
  assignableRoles: [],
  grantablePermissions: [],
};

/**
 * Three shapes live in `users.service_desk_id`: the bare `'*'`, one id, and a list. It is a JSONB
 * column, so an id can arrive as a string — which is why the backend compares desks through
 * `String()` (`serviceDeskUtils`). Coerced here, or a `'3'` would never match desk 3 and the card
 * would name it `#3`. An unreadable value drops out rather than crashing the card that draws it.
 */
const toDesk = (entry: unknown): number | '*' | null => {
  if (entry === '*') return '*';
  const id = Number(entry);
  return Number.isFinite(id) && entry !== null && entry !== '' ? id : null;
};

export const toDeskList = (value: unknown): DeskAssignment => {
  if (value === '*') return ['*'];
  const entries = Array.isArray(value) ? value : [value];
  return entries.map(toDesk).filter((entry): entry is number | '*' => entry !== null);
};

/** The decorated row, reduced to the rights half. Snake case stops at this boundary. */
const toRecord = (row: Record<string, any>): UserRightsRecord => ({
  role: String(row?.role ?? ''),
  rights: {
    serviceDeskId: toDeskList(row?.serviceDeskId),
    allowedTicketCreationDesks: Array.isArray(row?.allowed_ticket_creation_desks)
      ? row.allowed_ticket_creation_desks.map(Number).filter(Number.isFinite)
      : [],
    permissions: Array.isArray(row?.extraPermissions) ? row.extraPermissions.map(String) : [],
  },
  abilities: { ...ABILITIES, ...(row?.abilities ?? {}) },
});

/**
 * What a creation form starts on. The wildcard is a technician invariant — every account in
 * `backend/config/technicians_users.js` is cross-desk, and the record has no screen to widen it
 * later — so it is pre-picked.
 *
 * **The ticket-creation desks are not.** Opening tickets on a desk is a grant, and a grant nobody
 * chose is a grant nobody reviewed: a pre-ticked « L'ORÉAL LUXE » was read as a fact of the form
 * rather than as a decision. Empty, the picker asks the question out loud. The API agrees —
 * `TECHNICIAN_ACCOUNT` no longer defaults it either.
 */
export const TECHNICIAN_DEFAULT_RIGHTS: UserRights = {
  serviceDeskId: ['*'],
  allowedTicketCreationDesks: [],
  permissions: [],
};

/** The technician role's own line of the creation abilities: the only one this form can create. */
export const fetchCreationRights = async (): Promise<CreationRights> => {
  const data = await unwrap<Record<string, any>>(
    await fetchWithAuth('users/abilities'),
    'Failed to load what you may grant'
  );
  const pickable: string[] = Array.isArray(data?.serviceDeskPickableRoles) ? data.serviceDeskPickableRoles : [];
  return {
    canEditServiceDesks: pickable.includes('technician'),
    canGrantPermissions: data?.canGrantPermissions === true,
    grantablePermissions: Array.isArray(data?.grantablePermissions) ? data.grantablePermissions.map(String) : [],
  };
};

export const fetchUserRights = async (id: number): Promise<UserRightsRecord> =>
  toRecord(await unwrap<Record<string, any>>(await fetchWithAuth(`users/${id}`), 'Failed to load the account'));

/**
 * A deliberately partial write: the controller drops every absent field, so posting these three
 * leaves the identity, the language and the status exactly as the record's own PUT left them.
 */
export const updateUserRights = async (id: number, rights: UserRights): Promise<UserRightsRecord> =>
  toRecord(
    await unwrap<Record<string, any>>(
      await fetchWithAuth(`users/${id}`, { method: 'PUT', headers: jsonHeaders, body: JSON.stringify(rights) }),
      'Failed to save the rights'
    )
  );
